import React, { useEffect, useState } from "react";
import { X, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useSafeBackdropClose } from "../lib/modal";
import { api, formatApiError } from "../lib/api";
import { DEFAULT_CLASS } from "../lib/classification";
import { DEFAULT_RISK, resolveRisk } from "../lib/risk";
import ClassSelect from "./ClassSelect";
import RiskSelect from "./RiskSelect";
import DesignPreviewCard from "./DesignPreviewCard";
import { usePortfolioMetaOptions } from "../hooks/usePortfolioMetaOptions";
import DateInput from "./DateInput";

const STATUSES = [
  "design", "quote_draft", "quote_sent", "approved",
  "installing", "monitoring", "completed", "cancelled",
];

export default function EditProjectModal({ project, onClose, onSaved, onDeleted, canDelete }) {
  const [form, setForm] = useState({ ...project });
  const [users, setUsers] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [primaryDesign, setPrimaryDesign] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const metaOptions = usePortfolioMetaOptions();

  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/projects").then(r => {
      setParentOptions((r.data || []).filter(p => p.id !== project.id));
    }).catch(() => {});
    // Fetch the primary design to power the DesignPreviewCard inside
    // this modal. Endpoint returns a single design object (the primary
    // one) directly; falls back silently when the project doesn't have one.
    api.get(`/designs/by-project/${project.id}`)
      .then(r => setPrimaryDesign(r.data && r.data.id ? r.data : null))
      .catch(() => setPrimaryDesign(null));
  }, [project.id]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name, customer_name: form.customer_name, customer_email: form.customer_email,
        customer_phone: form.customer_phone, address: form.address, postcode: form.postcode,
        status: form.status, assigned_epc: form.assigned_epc || null,
        assigned_investor: form.assigned_investor || null, assigned_manager: form.assigned_manager || null,
        solaredge_site_id: form.solaredge_site_id, solaredge_api_key: form.solaredge_api_key,
        data_source: form.data_source || null,
        handover_date: form.handover_date, notes: form.notes,
        expected_completion_date: form.expected_completion_date ?? null,
        solaredge_design_url: form.solaredge_design_url ?? "",
        business_classification: form.business_classification || DEFAULT_CLASS,
        risk_status: resolveRisk(form.risk_status),
        // Portfolio metadata — blank string collapses server-side to null.
        unit_code: form.unit_code ?? "",
        property_code: form.property_code ?? "",
        developer: form.developer ?? "",
        operator: form.operator ?? "",
        panel_manufacturer: form.panel_manufacturer ?? "",
        inverter_manufacturer: form.inverter_manufacturer ?? "",
        // Empty string clears the parent link (unlink); the backend
        // normalises "" → null before the cycle check.
        parent_project_id: form.parent_project_id === undefined ? project.parent_project_id : (form.parent_project_id || ""),
        // Commercial model
        is_rentalised: !!form.is_rentalised,
        contracted_monthly_rent_gbp: form.contracted_monthly_rent_gbp === "" || form.contracted_monthly_rent_gbp == null
          ? null : Number(form.contracted_monthly_rent_gbp),
        fit_export: !!form.fit_export,
        fit_provider: form.fit_export ? (form.fit_provider || null) : null,
        fit_reference: form.fit_export ? (form.fit_reference || null) : null,
        fit_frequency_months: form.fit_export && form.fit_frequency_months
          ? Number(form.fit_frequency_months) : null,
      };
      const { data } = await api.patch(`/projects/${project.id}`, payload);
      toast.success("Project saved");
      onSaved && onSaved(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Project deleted");
      onDeleted && onDeleted();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  // ---- Close-guard: only dismiss when the ORIGINAL mousedown was on the
  // overlay backdrop itself. Prevents accidental close when a text
  // selection inside an input drags past the modal edge.
  const backdrop = useSafeBackdropClose(onClose);

  return (
    <div className="modal-overlay" {...backdrop} data-testid="edit-project-modal">
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label">Edit project</div>
            <h2 className="font-display text-2xl font-bold mt-1" data-testid="edit-project-title">{project.name}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="edit-project-close-btn"><X size={14} /></button>
        </div>

        <div className="p-5 space-y-5">
          {primaryDesign && (
            <DesignPreviewCard
              data={primaryDesign}
              testid="edit-design-preview"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Project name"><input className="input-swiss" value={form.name || ""} onChange={upd("name")} data-testid="edit-field-name" /></Field>
            <Field label="Status">
              <select className="input-box" value={form.status} onChange={upd("status")} data-testid="edit-field-status">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            <Field label="Customer name"><input className="input-swiss" value={form.customer_name || ""} onChange={upd("customer_name")} data-testid="edit-field-customer-name" /></Field>
            <Field label="Customer email"><input className="input-swiss" value={form.customer_email || ""} onChange={upd("customer_email")} data-testid="edit-field-customer-email" /></Field>
            <Field label="Business classification">
              <ClassSelect
                testId="edit-field-classification"
                value={form.business_classification || DEFAULT_CLASS}
                onChange={(v) => setForm((f) => ({ ...f, business_classification: v }))}
              />
            </Field>
            <Field label="Risk rating (RAG)">
              <RiskSelect
                testId="edit-field-risk"
                value={form.risk_status || DEFAULT_RISK}
                onChange={(v) => setForm((f) => ({ ...f, risk_status: v }))}
              />
            </Field>
            <Field label="Unit code">
              <input className="input-swiss" value={form.unit_code || ""} onChange={upd("unit_code")} data-testid="edit-field-unit-code" placeholder="e.g. U-24" />
            </Field>
            <Field label="Property code">
              <input className="input-swiss" value={form.property_code || ""} onChange={upd("property_code")} data-testid="edit-field-property-code" placeholder="e.g. PC-01" />
            </Field>
            <Field label="Developer">
              <input className="input-swiss" list="edit-developer-options" value={form.developer || ""} onChange={upd("developer")} data-testid="edit-field-developer" placeholder="e.g. Indurent" />
              <datalist id="edit-developer-options">
                {metaOptions.developer.map(v => <option key={v} value={v} />)}
              </datalist>
            </Field>
            <Field label="Operator">
              <input className="input-swiss" list="edit-operator-options" value={form.operator || ""} onChange={upd("operator")} data-testid="edit-field-operator" placeholder="e.g. Flow Energy" />
              <datalist id="edit-operator-options">
                {metaOptions.operator.map(v => <option key={v} value={v} />)}
              </datalist>
            </Field>
            <Field label="Panel manufacturer">
              <input className="input-swiss" list="edit-panel-options" value={form.panel_manufacturer || ""} onChange={upd("panel_manufacturer")} data-testid="edit-field-panel-mfr" placeholder="e.g. JinkoSolar" />
              <datalist id="edit-panel-options">
                {metaOptions.panel_manufacturer.map(v => <option key={v} value={v} />)}
              </datalist>
            </Field>
            <Field label="Inverter manufacturer">
              <input className="input-swiss" list="edit-inverter-options" value={form.inverter_manufacturer || ""} onChange={upd("inverter_manufacturer")} data-testid="edit-field-inverter-mfr" placeholder="e.g. SolarEdge" />
              <datalist id="edit-inverter-options">
                {metaOptions.inverter_manufacturer.map(v => <option key={v} value={v} />)}
              </datalist>
            </Field>
            <Field label="Customer phone"><input className="input-swiss" value={form.customer_phone || ""} onChange={upd("customer_phone")} /></Field>
            <Field label="Address"><input className="input-swiss" value={form.address || ""} onChange={upd("address")} data-testid="edit-field-address" /></Field>
            <Field label="Postcode"><input className="input-swiss" value={form.postcode || ""} onChange={upd("postcode")} /></Field>
            <Field label="Solar system type">
              <select className="input-box"
                      value={form.data_source || ""}
                      onChange={upd("data_source")}
                      data-testid="edit-field-system-type">
                <option value="">— pick a monitoring source —</option>
                <option value="solaredge">SolarEdge</option>
                <option value="solis">Solis (Ginlong SolisCloud)</option>
                <option value="meteronline">MeterOnline (HH meter)</option>
                <option value="other">Other / manual / no monitoring yet</option>
              </select>
            </Field>
            {form.data_source === "solaredge" && (
              <>
                <Field label="SolarEdge Site ID"><input className="input-swiss font-mono text-xs" value={form.solaredge_site_id || ""} onChange={upd("solaredge_site_id")} /></Field>
                <Field label="SolarEdge API key (override)"><input className="input-swiss font-mono text-xs" value={form.solaredge_api_key || ""} onChange={upd("solaredge_api_key")} placeholder="optional per-site" /></Field>
              </>
            )}
            {form.data_source !== "solaredge" && form.solaredge_site_id && (
              <Field label="SolarEdge Site ID (kept)"><input className="input-swiss font-mono text-xs" value={form.solaredge_site_id || ""} onChange={upd("solaredge_site_id")} /></Field>
            )}
            <Field label="Handover date"><DateInput className="input-box" value={form.handover_date || ""} onChange={upd("handover_date")} data-testid="edit-field-handover-date" /></Field>
            <Field label="Expected completion date"><DateInput className="input-box" value={form.expected_completion_date || ""} onChange={upd("expected_completion_date")} data-testid="edit-field-expected-completion" /></Field>
            <Field label="Parent site">
              <select className="input-box"
                      value={form.parent_project_id || ""}
                      onChange={upd("parent_project_id")}
                      data-testid="edit-field-parent-project">
                <option value="">— no parent (top-level site) —</option>
                {parentOptions.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.children_count ? ` (${p.children_count} sub-site${p.children_count === 1 ? "" : "s"})` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <UserSelect label="EPC / Installer" users={users.filter(u => u.role === "epc" || u.role === "admin")} value={form.assigned_epc} onChange={upd("assigned_epc")} />
            <UserSelect label="Investor" users={users.filter(u => u.role === "investor" || u.role === "admin")} value={form.assigned_investor} onChange={upd("assigned_investor")} />
            <UserSelect label="Project Manager" users={users.filter(u => u.role === "manager" || u.role === "admin")} value={form.assigned_manager} onChange={upd("assigned_manager")} />
          </div>
          <Field label="SolarEdge Designer URL (reference)">
            <input
              className="input-swiss font-mono text-xs"
              type="url"
              placeholder="https://designer.solaredge.com/sites/…"
              value={form.solaredge_design_url || ""}
              onChange={upd("solaredge_design_url")}
              data-testid="edit-field-solaredge-design-url"
            />
            <div className="text-[10px] muted mt-1">
              Optional. Shown on the project detail as a clickable link so anyone on the team can open the original design proposal.
            </div>
          </Field>
          <Field label="Notes">
            <textarea className="input-box" rows={3} value={form.notes || ""} onChange={upd("notes")} />
          </Field>
          <div className="md:col-span-2 border-t border-[var(--line)] pt-3 mt-1">
            <div className="label mb-2">Commercial model</div>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_rentalised}
                       onChange={(e) => setForm(f => ({ ...f, is_rentalised: e.target.checked }))}
                       data-testid="edit-rentalised-toggle" />
                <span className="text-sm">Rentalised site</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.fit_export}
                       onChange={(e) => setForm(f => ({ ...f, fit_export: e.target.checked }))}
                       data-testid="edit-fit-toggle" />
                <span className="text-sm">FIT export site</span>
              </label>
            </div>
            {form.is_rentalised && (
              <div className="mt-3 max-w-xs">
                <span className="label block mb-1">Contracted monthly rent £</span>
                <input type="number" step="0.01" className="input-box w-full"
                       value={form.contracted_monthly_rent_gbp ?? ""}
                       onChange={upd("contracted_monthly_rent_gbp")}
                       data-testid="edit-monthly-rent" />
              </div>
            )}
            {form.fit_export && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <span className="label block mb-1">FIT provider</span>
                  <input className="input-box w-full" value={form.fit_provider || ""}
                         onChange={upd("fit_provider")} data-testid="edit-fit-provider" />
                </div>
                <div>
                  <span className="label block mb-1">FIT reference</span>
                  <input className="input-box w-full" value={form.fit_reference || ""}
                         onChange={upd("fit_reference")} data-testid="edit-fit-reference" />
                </div>
                <div>
                  <span className="label block mb-1">Payment frequency</span>
                  <select className="input-box w-full" value={form.fit_frequency_months || ""}
                          onChange={upd("fit_frequency_months")} data-testid="edit-fit-frequency">
                    <option value="">—</option>
                    <option value="3">Every 3 months</option>
                    <option value="6">Every 6 months</option>
                    <option value="12">Every 12 months</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-t border-[var(--line)]">
          {canDelete ? (
            <button onClick={() => setConfirmDel(true)} className="btn-danger" disabled={busy} data-testid="edit-project-delete-btn">
              <Trash2 size={13} /> Delete project
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost" data-testid="edit-project-cancel-btn">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-accent" data-testid="edit-project-save-btn">
              <Save size={14} /> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {confirmDel && (
          <div className="modal-overlay" data-testid="confirm-delete-overlay">
            <div className="modal-card" style={{ maxWidth: 460 }}>
              <div className="p-6">
                <div className="label">Confirm</div>
                <h3 className="font-display text-2xl font-bold mt-1">Delete &ldquo;{project.name}&rdquo;?</h3>
                <p className="muted text-sm mt-3">This permanently deletes the project and all linked designs, quotes, tasks, documents and monitoring data. This cannot be undone.</p>
                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={() => setConfirmDel(false)} className="btn-ghost" data-testid="confirm-delete-cancel-btn">Keep project</button>
                  <button onClick={del} disabled={busy} className="btn-danger" data-testid="confirm-delete-yes-btn">
                    <Trash2 size={13} /> {busy ? "Deleting…" : "Delete permanently"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label block mb-1">{label}</span>
      {children}
    </label>
  );
}
function UserSelect({ label, users, value, onChange }) {
  return (
    <Field label={label}>
      <select className="input-box" value={value || ""} onChange={onChange}>
        <option value="">— unassigned —</option>
        {users.map(u => <option key={u.id} value={u.id}>{`${u.name} · ${u.email}`}</option>)}
      </select>
    </Field>
  );
}
